<template>
  <unblu-branch-client-app
    ref="unbluAppRef"
    v-if="apiKey && token && isUnbluApiLoaded"
    :api-key="apiKey"
    :branch-client-token="token"
    :server-url="environment.baseUrl"
    :entry-path="environment.entryPath"
    :named-area="namedArea"
    auto-init="false"
    locale="de"
    class="unblu-call-surface"
    :class="{ 'unblu-call-surface--active': effectiveInCall }"
  />

  <!-- Dextra UI always above idle Unblu (full-viewport Unblu stays z-index:-1 until CONNECTED) -->
  <div v-if="!effectiveInCall" class="dextra-shell">
    <MediaPermissionPrompt
      v-if="showMediaPrompt"
      :show="showMediaPrompt"
      @success="handleMediaPermissionSuccess"
      @skip="handleMediaPermissionSkip"
      @error="handleMediaPermissionError"
    />

    <template v-else-if="forcedComponent">
      <component :is="components[forcedComponent]" :api-key="apiKey" :token="token" />
    </template>

    <template v-else>
      <!-- AgentBusy v-if="showAgentBusy" :api-key="apiKey" :token="token" /> -->
      <!-- AgentQueue v-else-if="isQueued" :api-key="apiKey" :token="token" /> -->
      <AgentOffline v-if="isBranchForcedOffline" :api-key="apiKey" :token="token" />
      <AgentOnlineWithCallButton v-else-if="showCallScreen" :api-key="apiKey" :token="token" />
      <AgentConnecting v-else-if="isConnecting" :api-key="apiKey" :token="token" />
      <AgentOnline v-else-if="agentOnline" :api-key="apiKey" :token="token" @navigate-to-call="handleNavigateToCall" />
      <AgentOffline v-else :api-key="apiKey" :token="token" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, defineAsyncComponent, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBranchParams } from './composables/useBranchParams'
import { getCurrentEnvironment } from './config/environments'
import { useMediaPermissions } from './composables/useMediaPermissions'
import { logWithSuffix, logAlways } from './utils/clientLogger'
import { mediaPermissionManager } from './utils/mediaPermissions'

// Assets are imported by individual components as needed
// Import all agent components dynamically
const AgentOnline = defineAsyncComponent(() => import('./components/AgentOnline.vue'))
const AgentOffline = defineAsyncComponent(() => import('./components/AgentOffline.vue'))
const AgentConnecting = defineAsyncComponent(() => import('./components/AgentConnecting.vue'))
const AgentOnlineWithCallButton = defineAsyncComponent(() => import('./components/AgentOnlineWithCallButton.vue'))
const AGB = defineAsyncComponent(() => import('./components/AGB.vue'))
const Bewertung = defineAsyncComponent(() => import('./components/Bewertung.vue'))
const Abschluss = defineAsyncComponent(() => import('./components/Abschluss.vue'))
const MediaPermissionPrompt = defineAsyncComponent(() => import('./components/MediaPermissionPrompt.vue'))
const MediaTest = defineAsyncComponent(() => import('./components/MediaTest.vue'))
// const AgentBusy = defineAsyncComponent(() => import('./components/AgentBusy.vue'))
// const AgentQueue = defineAsyncComponent(() => import('./components/AgentQueue.vue'))

import { BranchClientViewHelper } from './unblu/BranchClientViewHelper'
import { loadUnbluApi } from './utils/loadUnbluApi'

const components: Record<string, any> = {
  AgentOnline,
  AgentOffline,
  AgentConnecting,
  AgentOnlineWithCallButton,
  AGB,
  Bewertung,
  Abschluss,
  MediaPermissionPrompt,
  MediaTest,
  // AgentBusy,
  // AgentQueue
}

// Keep browser tab title stable (Unblu or other scripts may clear it)
function setStableTitle(suffix?: string) {
  const meta = document.querySelector('meta[name="version"]') as HTMLMetaElement | null
  const version = meta?.content ? ` ${meta.content}` : ''
  const base = 'Dextra'
  document.title = suffix ? `${base}${version} – ${suffix}` : `${base}${version}`
}

const { apiKey, token } = useBranchParams()
const environment = getCurrentEnvironment()
const { locale } = useI18n()

// Media permissions
const {
  hasPermissions,
  checkPermissions,
  requestPermissions,
} = useMediaPermissions()

/**
 * Every page reload: check camera + microphone; if not granted, request (getUserMedia once, tracks released).
 */
const runInitialPermissionCheck = async () => {
  logWithSuffix('info', 'PERMISSION-RELOAD-CHECK', 'Page reload — checking camera and microphone permissions')
  const state = await checkPermissions()
  logWithSuffix('info', 'PERMISSION-RELOAD-RESULT', 'Camera and microphone check on reload', {
    camera: state.camera,
    microphone: state.microphone,
    hasPermissions: state.hasPermissions,
    hasCameraDevice: state.deviceAvailability?.hasCamera,
    hasMicrophoneDevice: state.deviceAvailability?.hasMicrophone,
  })
  if (state.hasPermissions) {
    mediaPermissionGranted.value = true
    return
  }
  if (state.camera === 'denied' || state.microphone === 'denied') {
    logWithSuffix('warn', 'PERMISSION-DENIED-RELOAD', 'Camera or microphone denied on reload', {
      camera: state.camera,
      microphone: state.microphone,
    })
    return
  }
  logWithSuffix('info', 'PERMISSION-REQUEST-RELOAD', 'Not granted on reload — requesting camera and microphone')
  try {
    const result = await requestPermissions()
    if (result.video && result.audio) {
      mediaPermissionGranted.value = true
      logWithSuffix('info', 'PERMISSION-GRANTED-RELOAD', 'Camera and microphone granted on reload')
    } else {
      logWithSuffix('warn', 'PERMISSION-REQUEST-RELOAD-INCOMPLETE', 'Reload permission request did not get both tracks', result)
    }
  } catch (error) {
    logWithSuffix('warn', 'PERMISSION-REQUEST-RELOAD-FAILED', 'Could not request camera/mic on reload:', error)
  }
}

const agentOnline = ref(false)
const agentBusy = ref(false)
const availabilityOnline = ref(false)
const availabilityBusy = ref(false)
const isConnecting = ref(false)
const isQueued = ref(false)
const showBusyAfterTimeout = ref(false)
const inCall = ref(false)
const showCallScreen = ref(false)
const isUnbluApiLoaded = ref(false)
const forcedComponent = ref<string | null>(null)
const branchState = ref<'ACTIVE' | 'MAINTENANCE' | 'INACTIVE' | 'UNSUPPORTED' | 'UNKNOWN'>('UNKNOWN')

// Media permission prompt state
const showMediaPrompt = ref(false)
const mediaPermissionGranted = ref(false)

// State memory to remember user's intent
const userIntent = ref<'none' | 'wants-to-call'>('none')
const lastActiveState = ref<'connecting' | 'queued' | 'busy' | null>(null)
const isCleaningUp = ref(false) // Flag to prevent call end detection during cleanup
const isTransitioningToAbschluss = ref(false) // Flag to prevent interference during transition to Abschluss
let abschlussTransitionTimer: number | undefined

const showAgentBusy = computed(() => isQueued.value && showBusyAfterTimeout.value)
const isBranchForcedOffline = computed(
  () => branchState.value === 'MAINTENANCE' || branchState.value === 'INACTIVE'
)
const effectiveInCall = computed(() => inCall.value && !isBranchForcedOffline.value)

/** True while a call is starting or active — pause probe getUserMedia / device checks. */
const isCallSessionActive = computed(
  () =>
    inCall.value ||
    isConnecting.value ||
    isQueued.value ||
    showCallScreen.value
)

// Watch for state changes and log them
watch([showAgentBusy, isQueued, isConnecting, agentOnline, inCall], () => {
  // The currentComponent computed property will log automatically
}, { immediate: true })

watch(agentOnline, (online, prev) => {
  if (prev !== undefined) {
    const status = online ? 'ONLINE' : 'OFFLINE'
    logAlways(`Agent is now ${status}`)
    logWithSuffix('warn', `Agent is now ${status}`, online ? 'AGENT-ONLINE' : 'AGENT-OFFLINE')
  }
})

watch(forcedComponent, (newValue, oldValue) => {
  console.debug(`FORCED COMPONENT CHANGED: ${oldValue} -> ${newValue}`)
}, { immediate: true })

watch([showCallScreen, isConnecting, agentOnline, forcedComponent], ([newShowCallScreen, newIsConnecting, newAgentOnline, newForcedComponent]) => {
  console.debug('COMPONENT STATE:', {
    showCallScreen: newShowCallScreen,
    isConnecting: newIsConnecting, 
    agentOnline: newAgentOnline,
    forcedComponent: newForcedComponent
  })
}, { immediate: true })

// Computed property to determine which component is being shown
const currentComponent = computed(() => {
  if (forcedComponent.value) {
    return `FORCED: ${forcedComponent.value}`
  }
  if (showCallScreen.value) {
    return 'AgentOnlineWithCallButton'
  }
  if (isConnecting.value) {
    return 'AgentConnecting'
  }
  if (agentOnline.value) {
    return 'AgentOnline'
  }
  return 'AgentOffline'
})

watch(currentComponent, (newComponent) => {
  console.debug('CURRENT COMPONENT:', newComponent)
}, { immediate: true })

// Add computed property for named area
const namedArea = computed(() => {
  const baseUrl = environment.baseUrl
  const path = window.location.pathname
  
  console.debug(`Environment baseUrl: ${baseUrl}`)
  console.debug(`Current path: ${path}`)
  
  // Dextra uses Beratung named area
  return 'Beratung'
})

const ALLOW_BUSY_QUEUE = true
let lastLanguageAvailabilitySummary = ''
let branchMonitorTimer: number | undefined
let latestAvailabilitySnapshot: any = []
type NormalizedArea = {
  label: string
  availability: 'AVAILABLE' | 'BUSY' | 'UNAVAILABLE'
  language?: string
}

function normalizeBranchState(raw: any): 'ACTIVE' | 'MAINTENANCE' | 'INACTIVE' | 'UNSUPPORTED' | 'UNKNOWN' {
  const candidate = String(raw?.state || raw?.status || raw || '').toUpperCase()
  if (candidate === 'ACTIVE' || candidate === 'MAINTENANCE' || candidate === 'INACTIVE') return candidate
  return 'UNKNOWN'
}

function branchAllowsOnline(state: typeof branchState.value = branchState.value): boolean {
  // Unsupported/unknown branch state must not hard-block availability logic.
  return state === 'ACTIVE' || state === 'UNSUPPORTED' || state === 'UNKNOWN'
}

function clearTransientCallQueueState(): void {
  if (connectionTimeout) {
    clearTimeout(connectionTimeout)
    connectionTimeout = undefined
  }
  if (queuedTimer) {
    clearTimeout(queuedTimer)
    queuedTimer = undefined
  }
  inCall.value = false
  showCallScreen.value = false
  isConnecting.value = false
  isQueued.value = false
  showBusyAfterTimeout.value = false
  queuedSince = null
  userIntent.value = 'none'
  lastActiveState.value = null
  try {
    window.sessionStorage.removeItem('connectingSince')
  } catch {
    // ignore storage failures
  }
}

function applyEffectiveAgentState(): void {
  const gatedOnline = branchAllowsOnline() && availabilityOnline.value
  const gatedBusy = branchAllowsOnline() && availabilityBusy.value
  agentOnline.value = gatedOnline
  agentBusy.value = gatedBusy
}

async function pollBranchState(api: any, source: 'INITIAL' | 'POLL'): Promise<void> {
  if (typeof api?.getBranchState !== 'function') {
    if (branchState.value !== 'UNSUPPORTED') {
      const previous = branchState.value
      branchState.value = 'UNSUPPORTED'
      logAlways(`BRANCH_STATE_CHANGE (${source}): ${previous} -> UNSUPPORTED`)
    }
    applyEffectiveAgentState()
    return
  }

  try {
    const rawState = await api.getBranchState()
    const nextState = normalizeBranchState(rawState)
    const previous = branchState.value
    branchState.value = nextState
    applyEffectiveAgentState()

    if (previous !== nextState) {
      logAlways(`BRANCH_STATE_CHANGE (${source}): ${previous} -> ${nextState}`)
      if (nextState === 'MAINTENANCE' || nextState === 'INACTIVE') {
        clearTransientCallQueueState()
      }
      // Reprint service-state line with updated branch state gate.
      lastLanguageAvailabilitySummary = ''
      applyAgentAvailability(latestAvailabilitySnapshot, 'branch-state-change')
    }
  } catch (error) {
    logWithSuffix('warn', 'BRANCH-STATE-READ-FAILED', 'Failed to read branch state:', error)
  }
}

function normalizeArea(raw: any): NormalizedArea {
  const label = raw?.displayName || raw?.name || raw?.namedAreaId || raw?.namedAreaSiteId || 'unknown'
  const availability =
    raw?.availability === 'AVAILABLE' || raw?.availability === 'BUSY'
      ? raw.availability
      : 'UNAVAILABLE'
  const language = raw?.languageTag || raw?.locale
  return { label, availability, language }
}

function applyAgentAvailability(rawAreas: any, source: string): void {
  latestAvailabilitySnapshot = rawAreas
  const rawList: any[] = Array.isArray(rawAreas)
    ? rawAreas
    : Array.isArray(rawAreas?.areas)
      ? rawAreas.areas
      : Array.isArray(rawAreas?.availabilities)
        ? rawAreas.availabilities
        : []
  const areas: NormalizedArea[] = rawList.map(normalizeArea)
  const requestedNamedArea = namedArea.value
  const scopedAreas: NormalizedArea[] = requestedNamedArea
    ? areas.filter(a => a.label === requestedNamedArea)
    : areas
  const requestedLanguage = String(locale.value || '').toLowerCase()
  const hasLanguageData = scopedAreas.some(a => Boolean(a.language))
  const languageScopedAreas = hasLanguageData
    ? scopedAreas.filter(a => String(a.language || '').toLowerCase() === requestedLanguage)
    : scopedAreas
  const effectiveAreas = languageScopedAreas

  if (!scopedAreas.length) {
    console.warn(`No matching areas found for "${requestedNamedArea}" in ${source}`)
    availabilityOnline.value = false
    availabilityBusy.value = false
    applyEffectiveAgentState()
    return
  }
  if (hasLanguageData && !effectiveAreas.length) {
    console.warn(
      `No matching language queue for "${requestedLanguage}" in "${requestedNamedArea}" (${source})`
    )
    availabilityOnline.value = false
    availabilityBusy.value = false
    applyEffectiveAgentState()
    return
  }

  const hasAvailable = effectiveAreas.some(a => a.availability === 'AVAILABLE')
  const hasBusy = effectiveAreas.some(a => a.availability === 'BUSY')
  availabilityOnline.value = hasAvailable || (ALLOW_BUSY_QUEUE && hasBusy)
  availabilityBusy.value = !hasAvailable && hasBusy
  applyEffectiveAgentState()

  const byArea = new Map<string, { available: Set<string>; busy: Set<string>; unavailable: Set<string> }>()
  for (const area of scopedAreas) {
    if (!byArea.has(area.label)) {
      byArea.set(area.label, { available: new Set(), busy: new Set(), unavailable: new Set() })
    }
    const bucket = byArea.get(area.label)!
    const lang = area.language || '-'
    if (area.availability === 'AVAILABLE') bucket.available.add(lang)
    else if (area.availability === 'BUSY') bucket.busy.add(lang)
    else bucket.unavailable.add(lang)
  }

  const summary = Array.from(byArea.entries()).map(([label, bucket]) => {
    const effectiveStatus =
      bucket.available.size > 0 ? 'AVAILABLE' : bucket.busy.size > 0 ? 'BUSY' : 'UNAVAILABLE'
    const langs =
      effectiveStatus === 'AVAILABLE'
        ? Array.from(bucket.available)
        : effectiveStatus === 'BUSY'
          ? Array.from(bucket.busy)
          : Array.from(bucket.unavailable)
    return `${label}=${effectiveStatus} [${langs.join(',') || '-'}]`
  })
  const agentState = !branchAllowsOnline()
    ? 'OFFLINE'
    : hasAvailable
      ? 'ONLINE'
      : hasBusy
        ? 'BUSY'
        : 'OFFLINE'
  const summaryText =
    `SERVICE_STATE_CHANGE: ${summary.join(', ')} | Agent: ${agentState} | Branch: ${branchState.value}` +
    (requestedLanguage ? ` | Requested language: ${requestedLanguage}` : '')
  if (summaryText !== lastLanguageAvailabilitySummary) {
    lastLanguageAvailabilitySummary = summaryText
    logAlways(summaryText)
  }
}

// Restore the simple toggle for convenience
const toggleAgentStatus = () => {
  setAgentState(agentOnline.value ? 'offline' : 'online')
}

// Force agent online for testing
const forceAgentOnline = () => {
  agentOnline.value = true
  console.debug('Agent forced online for testing')
}

// Manual state controller for preview purposes
const setAgentState = (state: 'online' | 'offline' | 'connecting' | 'queued' | 'busy' | 'start' | 'bewertung' | 'abschluss' | 'agb' | 'agentonlinewithcallbutton') => {
  // Reset all states first (except agentOnline for 'start' state)
  if (state !== 'start') {
    agentOnline.value = false
  }
  isConnecting.value = false
  isQueued.value = false
  showBusyAfterTimeout.value = false
  inCall.value = false // Ensure computed properties are not affected
  showCallScreen.value = false

  switch (state.toLowerCase()) {
    case 'online':
      agentOnline.value = true
      break
    case 'offline':
      // All states are already false, nothing more to do
      break
    case 'connecting':
      isConnecting.value = true
      break
    case 'queued':
      isQueued.value = true // This will display AgentConnecting
      break
    case 'busy':
      showBusyAfterTimeout.value = true
      break
    case 'start':
      // Return to normal flow (AgentOnline or AgentOffline)
      forcedComponent.value = null
      // Ensure transition guard is cleared so events are processed
      isTransitioningToAbschluss.value = false
      break
    case 'bewertung':
      forcedComponent.value = 'Bewertung'
      break
    case 'abschluss':
      forcedComponent.value = 'Abschluss'
      break
    case 'agb':
      forcedComponent.value = 'AGB'
      break
    case 'agentonlinewithcallbutton':
      forcedComponent.value = 'AgentOnlineWithCallButton'
      break
  }
}

// Handle navigation to call screen
const handleNavigateToCall = async () => {
  if (isBranchForcedOffline.value) {
    console.warn(`Blocked call navigation because branch state is ${branchState.value}`)
    return
  }
  await checkPermissions()
  if (hasPermissions.value) {
    mediaPermissionGranted.value = true
    showCallScreen.value = true
    setStableTitle('Verbinden…')
    return
  }
  if (!mediaPermissionGranted.value) {
    console.debug('Media permissions needed, showing prompt (getUserMedia on user action only)')
    showMediaPrompt.value = true
    return
  }

  showCallScreen.value = true
  setStableTitle('Verbinden…')
}

// Media permission handlers
const handleMediaPermissionSuccess = () => {
  console.debug('Media permissions granted successfully')
  mediaPermissionGranted.value = true
  showMediaPrompt.value = false
  
  // Now proceed with call screen
  showCallScreen.value = true
  setStableTitle('Verbinden…')
}

const handleMediaPermissionSkip = () => {
  console.debug('Media permissions skipped by user')
  showMediaPrompt.value = false
  
  // Still proceed with call screen (audio-only fallback)
  showCallScreen.value = true
  setStableTitle('Verbinden…')
}

const handleMediaPermissionError = (error: string) => {
  console.warn('Media permission error (logged only):', error)
  logWithSuffix('warn', 'MEDIA-PERMISSION-ERROR', 'Media permission error:', error)
  showMediaPrompt.value = false
  // Proceed to call screen (audio-only fallback); no blocking overlay
  showCallScreen.value = true
  setStableTitle('Verbinden…')
}

// Graceful call end handler
const handleCallEndGracefully = () => {
  console.log('🎬 Starting graceful call end transition...')
  setStableTitle('Call ended')
  
  // First, show a brief "Call ended" message
  inCall.value = false
  showCallScreen.value = false
  isConnecting.value = false
  isQueued.value = false
  showBusyAfterTimeout.value = false
  
  // Show a brief transition message before going to Abschluss
  setTimeout(() => {
    console.log('📋 Transitioning to conclusion screen...')
    forcedComponent.value = 'Abschluss'
    
    // Reset media permission state for next call
    mediaPermissionGranted.value = false
    showMediaPrompt.value = false
    
    // Clear transition guard now that Abschluss is shown
    isTransitioningToAbschluss.value = false
    if (abschlussTransitionTimer) {
      clearTimeout(abschlussTransitionTimer)
      abschlussTransitionTimer = undefined
    }
    setStableTitle('Abschluss')
    
    console.log('Call end transition complete')
  }, 1000) // 1 second delay for smooth transition
}

// Reset call screen state
const resetCallScreen = () => {
  console.warn('Resetting call screen state...')
  showCallScreen.value = false
  isConnecting.value = false
  isQueued.value = false
  showBusyAfterTimeout.value = false
  userIntent.value = 'none'
  lastActiveState.value = null
  
  // Force agent to be online since we know it's available
  agentOnline.value = true
  console.warn('Agent forced online after reset')
  setStableTitle()
}

// --- Simple View Cycler ---
const componentCycle = [null, 'AgentOnline', 'AgentOffline', 'AgentConnecting', 'AgentOnlineWithCallButton', 'AGB', 'Bewertung', 'Abschluss', 'MediaTest'];
let cycleIndex = 0;

const toggleNextView = () => {
  cycleIndex = (cycleIndex + 1) % componentCycle.length;
  const componentName = componentCycle[cycleIndex];
  showAgentComponent(componentName);
  return componentName; // Always return the component name, even if null
};
// -------------------------

// Direct component preview function
const showAgentComponent = (componentName: string | null) => {
  if (componentName && !components[componentName]) {
    console.error(`Component "${componentName}" not found. Available components:`, Object.keys(components))
    return
  }
  forcedComponent.value = componentName
  if (componentName) {
    console.debug(`Forcing preview of component: ${componentName}`)
  } else {
    console.debug('Returning to normal application view')
  }
}

// Expose functions to window for console access
if (typeof window !== 'undefined') {
  ;(window as any).toggleAgentStatus = toggleAgentStatus
  ;(window as any).setAgentState = setAgentState
  ;(window as any).resetCallScreen = resetCallScreen
  ;(window as any).forceAgentOnline = forceAgentOnline
  ;(window as any).showAgentComponent = showAgentComponent
  ;(window as any).toggleNextView = toggleNextView
  ;(window as any).cycleIndex = cycleIndex
  ;(window as any).resetMediaPermissions = () => {
    mediaPermissionGranted.value = false
    showMediaPrompt.value = false
    console.debug('Media permissions reset')
  }

  console.debug('Debug tools: showAgentComponent(), toggleAgentStatus(), toggleNextView(), setAgentState()')
}

const unbluAppRef = ref<HTMLUnbluBranchClientAppElement | null>(null)

let initialized = false
let queuedSince: number | null = null
let queuedTimer: number | undefined
let retryCount = 0
const MAX_RETRIES = 3
let connectionTimeout: number | undefined
const CONNECTION_TIMEOUT = 60000 // 60 seconds timeout for connection

let helper: BranchClientViewHelper | null = null
let softReinitInProgress = false

const clearCallTimers = () => {
  if (connectionTimeout) {
    clearTimeout(connectionTimeout)
    connectionTimeout = undefined
  }
  if (queuedTimer) {
    clearTimeout(queuedTimer)
    queuedTimer = undefined
  }
}

/** Re-init Unblu in-page (no full reload) — keeps WebRTC/LiveKit session stable when possible. */
const softReinitUnblu = async () => {
  if (softReinitInProgress) return
  softReinitInProgress = true
  console.debug('Soft reinitializing Unblu (no page reload)...')

  try {
    clearCallTimers()
    clearTransientCallQueueState()

    if (helper) {
      try {
        await helper.destroy()
      } catch (e) {
        console.debug('Helper destroy error (expected):', e)
      }
      helper = null
    }
    ;(window as any).unbluapi = null

    retryCount = 0
    setTimeout(() => {
      softReinitInProgress = false
      initializeUnblu()
    }, 500)
  } catch (error) {
    softReinitInProgress = false
    console.error('Soft reinit failed, reloading page:', error)
    window.location.reload()
  }
}

const initializeUnblu = async () => {
  try {
    console.debug('Starting Unblu initialization...')
    
    let attempts = 0
    const maxAttempts = 50
    
    while (!unbluAppRef.value && attempts < maxAttempts) {
      console.debug(`Waiting for Unblu element... (${attempts + 1}/${maxAttempts})`)
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }
    
    if (!unbluAppRef.value) {
      throw new Error('Unblu element not available after waiting')
    }
    
    const el = unbluAppRef.value as any
    if (!el?.initialize) {
      throw new Error('Unblu client not ready')
    }

    console.debug('Initializing Unblu API...')
    const api = await el.initialize()
    console.debug('Unblu API initialized')
    ;(window as any).unbluapi = api

    console.debug('Creating BranchClientViewHelper...')
    helper = new BranchClientViewHelper(api)
    await helper.init()
    console.debug('BranchClientViewHelper initialized')
    
    ;(window as any).helper = helper
    ;(window as any).debugHelper = () => {
      console.log('Helper object:', helper)
      console.log('Helper type:', typeof helper)
      if (helper && typeof (helper as any).debugApiMethods === 'function') {
        ;(helper as any).debugApiMethods()
      } else {
        console.log('debugApiMethods not available on helper')
      }
    }
    
    ;(window as any).debugUnbluApi = () => {
      console.log('Unblu API object:', (window as any).unbluapi)
      console.log('Unblu API type:', typeof (window as any).unbluapi)
    }

    ;(window as any).setUnbluLogLevel = (level = 'INFO') => {
      const unbluKeys = Object.keys(window).filter(key => key.startsWith('_unblu_'))
      if (unbluKeys.length > 0) {
        const unbluInstance = (window as any)[unbluKeys[0]]
        if (unbluInstance && typeof unbluInstance.setLogLevel === 'function') {
          console.log(`Setting Unblu log level to ${level} using key: ${unbluKeys[0]}`)
          unbluInstance.setLogLevel(level)
          return true
        }
      }
      console.warn('No Unblu instance with setLogLevel found')
      return false
    }

    retryCount = 0

    let areas: any = []
    if (typeof (api as any).getAgentAvailabilities === 'function') {
      try {
        areas = await (api as any).getAgentAvailabilities()
      } catch {
        areas = await api.getAvailableNamedAreas?.()
      }
    } else {
      areas = await api.getAvailableNamedAreas?.()
    }
    applyAgentAvailability(areas, 'init')

    await pollBranchState(api, 'INITIAL')
    if (branchMonitorTimer) clearInterval(branchMonitorTimer)
    branchMonitorTimer = window.setInterval(() => {
      pollBranchState(api, 'POLL')
    }, 3000)

    // --- READY banner ---
    const version = document.querySelector('meta[name="version"]')?.getAttribute('content') || '?'
    const buildTs = document.querySelector('meta[name="build-timestamp"]')?.getAttribute('content') || ''
    const permState = mediaPermissionManager.getPermissionState()
    const permStatus = permState.hasPermissions ? 'granted' : `cam:${permState.camera} mic:${permState.microphone}`
    let deviceSummary = ''
    try {
      const devs = await navigator.mediaDevices.enumerateDevices()
      const cams = devs.filter(d => d.kind === 'videoinput').length
      const mics = devs.filter(d => d.kind === 'audioinput').length
      const outs = devs.filter(d => d.kind === 'audiooutput').length
      deviceSummary = `${cams} cameras, ${mics} microphones, ${outs} audio outputs`
    } catch { deviceSummary = 'unavailable' }
    let branchKey = '?'
    try {
      if (token) {
        const parts = token.split('.')
        if (parts.length === 3) {
          const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
          const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)))
          branchKey = payload.branchClientKey || '?'
        }
      }
    } catch { /* ignore decode errors */ }
    const currentUrl = new URL(window.location.href)
    const cleanUrl = currentUrl.origin + currentUrl.pathname
    logAlways(
      `\n✓ READY\n` +
      `  Version:       ${version}\n` +
      `  Build time:    ${buildTs}\n` +
      `  Environment:   ${environment.name} | ${environment.baseUrl}\n` +
      `  URL:           ${cleanUrl}\n` +
      `  API key:       ${apiKey}\n` +
      `  Devices:       ${deviceSummary}\n` +
      `  Device perms:  ${permStatus}\n` +
      `  Agent:         ${agentOnline.value ? 'Online' : 'Offline'}\n` +
      `  Branch state:  ${branchState.value}\n` +
      `  Named area:    ${namedArea.value}\n` +
      `  Branch:        ${branchKey}\n`
    )



    helper.on(BranchClientViewHelper.EVENTS.CALL_STATE_CHANGE, ({ newCallState }) => {
      console.warn('CALL_STATE_CHANGE:', newCallState.name)
      console.debug('States before update:', { isConnecting: isConnecting.value, isQueued: isQueued.value, showBusyAfterTimeout: showBusyAfterTimeout.value, inCall: inCall.value })

      if (isBranchForcedOffline.value) {
        clearTransientCallQueueState()
        setStableTitle()
        console.warn(`Ignoring call state while branch is ${branchState.value}`)
        return
      }

      const isCallEnding = (newCallState.name === 'ENDED' || newCallState.name === 'CLOSED' || 
                           (newCallState.name === 'INIT' && inCall.value)) && !isCleaningUp.value
      
      if (isCallEnding) {
        console.warn('CALL ENDED - starting graceful transition to conclusion')
        console.debug('Call end from state:', newCallState.name, { inCall: inCall.value, showCallScreen: showCallScreen.value, forcedComponent: forcedComponent.value })
        
        // Set transition flag to prevent interference
        isTransitioningToAbschluss.value = true
        // Safety: auto-clear transition flag after 3s
        if (abschlussTransitionTimer) clearTimeout(abschlussTransitionTimer)
        abschlussTransitionTimer = window.setTimeout(() => {
          isTransitioningToAbschluss.value = false
        }, 3000)
        
        // Reset user intent when call ends
        userIntent.value = 'none'
        lastActiveState.value = null
        
        // Start graceful call end transition
        handleCallEndGracefully()
        
        // Return early to prevent other state changes
        return
      }

      inCall.value = newCallState.name === 'CONNECTED'
      isConnecting.value = newCallState.name === 'CONNECTING' || newCallState.name === 'QUEUED'

      // Clear connection timeout when not connecting
      if (newCallState.name !== 'CONNECTING') {
        if (connectionTimeout) {
          clearTimeout(connectionTimeout)
          connectionTimeout = undefined
          console.debug('Connection timeout cleared')
        }
      }

      if (newCallState.name === 'CONNECTING') {
        console.debug(`Starting connection timeout (${CONNECTION_TIMEOUT / 1000}s)`)
        if (connectionTimeout) clearTimeout(connectionTimeout)
        connectionTimeout = window.setTimeout(() => {
          console.warn('Connection timeout - soft reinit Unblu')
          userIntent.value = 'none'
          lastActiveState.value = null
          showCallScreen.value = false
          softReinitUnblu()
        }, CONNECTION_TIMEOUT)

        userIntent.value = 'wants-to-call'
        lastActiveState.value = 'connecting'
      }

      if (newCallState.name === 'QUEUED') {
        console.debug('Entering QUEUED state - starting 20s timer')
        isQueued.value = true
        queuedSince = Date.now()
        showBusyAfterTimeout.value = false
        lastActiveState.value = 'queued'

        if (queuedTimer) clearTimeout(queuedTimer)
        queuedTimer = window.setTimeout(() => {
          console.debug('Queue timeout reached (20s) - showing busy state')
          if (isQueued.value && Date.now() - (queuedSince || 0) >= 20000) {
            showBusyAfterTimeout.value = true
            lastActiveState.value = 'busy'
            console.debug('Busy state activated')
          }
        }, 20000)
        setStableTitle('Warteschlange')

      } else {
        if (isQueued.value) {
          console.debug('Exiting QUEUED state')
        }
        isQueued.value = false
        showBusyAfterTimeout.value = false
        queuedSince = null
        if (queuedTimer) {
          clearTimeout(queuedTimer)
          console.debug('Queue timer cleared')
        }
        setStableTitle()
      }

      if (newCallState.name === 'FAILED' || newCallState.name === 'DISCONNECTED') {
        const wasAttemptingCall =
          userIntent.value === 'wants-to-call' || showCallScreen.value
        console.warn('Connection failed or disconnected - falling back to offline state')
        isConnecting.value = false
        isQueued.value = false
        showBusyAfterTimeout.value = false
        inCall.value = false

        if (
          wasAttemptingCall &&
          !isCleaningUp.value &&
          !isTransitioningToAbschluss.value &&
          forcedComponent.value !== 'Abschluss'
        ) {
          setTimeout(() => {
            console.debug('Attempting soft Unblu reinit after call failure...')
            softReinitUnblu()
          }, 5000)
        }
      }

      if (newCallState.name === 'CONNECTED') {
        console.debug('Call connected - user intent fulfilled')
        userIntent.value = 'none'
        lastActiveState.value = null
      }

      console.debug('States after update:', { isConnecting: isConnecting.value, isQueued: isQueued.value, showBusyAfterTimeout: showBusyAfterTimeout.value, inCall: inCall.value })
    })

    helper.on(BranchClientViewHelper.EVENTS.SERVICE_STATE_CHANGE, (event: any) => {
      console.debug('SERVICE_STATE_CHANGE:', event)
      
      // Only guard when actually showing Abschluss AND in transition
      if (forcedComponent.value === 'Abschluss' && isTransitioningToAbschluss.value) {
        console.warn('SKIPPING SERVICE_STATE_CHANGE - transitioning to Abschluss')
        return
      }
      setStableTitle()
      applyAgentAvailability(event, 'event')

      // If agents become available, reset busy timeout state
      if (agentOnline.value && showBusyAfterTimeout.value) {
        console.debug('Agents became available - resetting busy timeout state')
        showBusyAfterTimeout.value = false
      }

      console.debug(`Agent status: ${agentOnline.value ? 'Online' : 'Offline'}`)
    })
  } catch (err: any) {
    const msg = err?.message ?? String(err)
    // Log only; no blocking overlay so users can keep using the app
    console.warn('Unblu API init failed (logged only):', msg)
    logWithSuffix('warn', 'UNBLU-INIT-FAILED', 'Unblu API init failed:', msg)

    // Retry logic
    if (retryCount < MAX_RETRIES) {
      retryCount++
      console.debug(`Retrying initialization (${retryCount}/${MAX_RETRIES})...`)
      setTimeout(initializeUnblu, 2000) // Retry after 2 seconds
    }
  }
}

onMounted(async () => {
  if (initialized) return
  initialized = true

  try {
    await loadUnbluApi()
    isUnbluApiLoaded.value = true
    setTimeout(initializeUnblu, 200)
    
    await runInitialPermissionCheck()

    // Adaptive permission polling: 2m → 3m → 5m when stable (resets to 2m on state change)
    let pollingInterval = 120000 // Start at 2 minutes
    const intervals = [120000, 180000, 300000] // 2m, 3m, 5m (max)
    let intervalIndex = 0
    let lastStateHash = ''
    let stableCheckCount = 0
    const STABLE_CHECKS_REQUIRED = 10

    const DEVICE_LIST_INTERVAL = 300000 // Device lists every 5 minutes when idle (enumerate only, no getUserMedia)
    let lastDeviceListCheck = Date.now()

    const adaptivePoll = async () => {
      try {
        if (isCallSessionActive.value) {
          setTimeout(adaptivePoll, pollingInterval)
          return
        }

        const now = Date.now()
        const showDeviceLists = now - lastDeviceListCheck >= DEVICE_LIST_INTERVAL
        if (showDeviceLists) {
          lastDeviceListCheck = now
        }

        const state = await checkPermissions(!showDeviceLists)
        if (state.hasPermissions) {
          mediaPermissionGranted.value = true
        }

        const currentStateHash = `${state.camera}-${state.microphone}-${state.deviceAvailability?.hasCamera}-${state.deviceAvailability?.hasMicrophone}`
        
        // If state changed, reset to fastest interval and reset stable count
        if (currentStateHash !== lastStateHash && lastStateHash !== '') {
          intervalIndex = 0
          pollingInterval = intervals[0]
          stableCheckCount = 0
        } else {
          // State unchanged - increment stable check count
          stableCheckCount++
          
          // Only increase interval after multiple consecutive stable checks
          // This prevents premature increase during initialization when there's a lot of activity
          if (stableCheckCount >= STABLE_CHECKS_REQUIRED && intervalIndex < intervals.length - 1) {
            intervalIndex++
            pollingInterval = intervals[intervalIndex]
            stableCheckCount = 0 // Reset counter after increasing
          }
        }
        
        lastStateHash = currentStateHash
        
        // Schedule next poll with current interval
        setTimeout(adaptivePoll, pollingInterval)
      } catch (error) {
        logWithSuffix('warn', 'PERIODIC-CHECK-FAILED', 'Periodic permission check failed:', error)
        // On error, reset to fastest interval and retry
        intervalIndex = 0
        pollingInterval = intervals[0]
        stableCheckCount = 0
        setTimeout(adaptivePoll, pollingInterval)
      }
    }
    
    // Start adaptive polling after initial check
    setTimeout(adaptivePoll, pollingInterval)
    
    const logitechCheckInterval = 600000 // 10 minutes (idle only)
    const logitechCheck = async () => {
      try {
        if (isCallSessionActive.value) {
          setTimeout(logitechCheck, logitechCheckInterval)
          return
        }
        // Check device availability in quiet mode (only Logitech warning will show)
        await mediaPermissionManager.checkDeviceAvailability(true)
      } catch (error) {
        console.warn('Logitech camera check failed:', error)
      }
      setTimeout(logitechCheck, logitechCheckInterval)
    }
    // Start Logitech check after initial check
    setTimeout(logitechCheck, logitechCheckInterval)
  } catch (error) {
    console.warn('Failed to load Unblu API (logged only):', error)
    logWithSuffix('warn', 'UNBLU-API-LOAD-FAILED', 'Failed to load Unblu API:', error)
  }
})
</script>

<style scoped>
/* Full viewport always (stable for Unblu/LiveKit); behind Dextra until in call. */
.unblu-call-surface {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: -1;
  opacity: 0;
  pointer-events: none;
}

.unblu-call-surface--active {
  z-index: 9999;
  opacity: 1;
  pointer-events: auto;
}

.dextra-shell {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10;
  overflow: auto;
  background-color: #fff;
}
</style>
